/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery, type CustomUseQueryOptions } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import type {
  ProductData,
  ProductDetailsOptions,
  ProductDetailsOptionsACES,
  ProductResponse,
} from '../interface';
import { getAxios } from '@/lib/axios';
import {
  type GetProductUsingGetParamsModel,
  type GetProductUsingGetParamsModelACES,
  type ProductModelACES,
  type ProductModel,
} from '@/api/types/products-types';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import type { QueryClient } from '@tanstack/react-query';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import { getProductDetailsURL } from '../utils/getProductDetailsURL';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useFeatureFlag } from '@/features/globalConfig';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { getDuplicateSkuQueryParams } from '@/utils/getDuplicateSkuQueryParams';
import environmentConfig from '@/config/environment';

// PartsPro (V1) - TODO: remove after 100% ACES/PIES transition
const URL = `${requestBaseURL}/external/product-discovery/product/v1/products`;

const getProductDetails = async (options: ProductDetailsOptions, axiosInstance?: AxiosInstance) => {
  const {
    locale,
    itemIds,
    vehicleId,
    storeId,
    partNumber,
    userSegment,
    productDetailsPageUrl,
    lineCode,
    preview,
  } = options;

  const country = getCountryFromLocale(locale);
  const salesChannel = 'ECOMM';
  const customerType = 'B2C';

  const params: GetProductUsingGetParamsModel = {
    country,
    salesChannel,
    customerType,
    itemIds,
    vehicleId,
    storeId,
    partNumber,
    userSegment,
    productDetailsPageUrl,
    preview,
    lineCode,
  };
  const response = await getAxios(axiosInstance).get<ProductResponse>(URL, { params });
  return selector(response.data);
};

const {
  useData,
  prefetch: prefetchProductDetails,
  getFromCache,
} = createQuery<ProductData, ProductDetailsOptions>('productDetails', getProductDetails);

const getProductDetailsFromCache = (
  queryClient: QueryClient,
  options: ProductDetailsOptions,
): ProductData | undefined => {
  return getFromCache(queryClient, options);
};

const selector = (data: ProductResponse): ProductData => {
  return {
    product: data.pdpResults?.products?.[0],
    redirectPageType: data.redirectPageType,
    redirectURL: data.redirectURL,
    xFusionQueryId: data.xFusionQueryId,
  };
};

// ACES (V2)
const V1_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/products`;
const V2_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v2/products`;

export const getProductDetailsACES = async (
  options: ProductDetailsOptionsACES,
  axiosInstance?: AxiosInstance,
) => {
  const {
    country,
    preview,
    productDetailsPageUrl,
    partGroupId,
    brandId,
    positionId,
    uniqueId,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    storeId,
    userSegment,
    isV2ProductsEnabled,
  } = options;

  const params: GetProductUsingGetParamsModelACES = {
    country,
    preview,
    productDetailsPageUrl,
    partGroupId,
    brandId,
    positionId,
    uniqueId,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    storeId,
    userSegment,
  };

  const URL = country === 'BRA' || isV2ProductsEnabled ? V2_URL : V1_URL;

  const response = await getAxios(axiosInstance).get<ProductResponse>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return acesSelector(response.data, options);
};

const {
  useData: useDataACES,
  prefetch: prefetchProductDetailsACES,
  getFromCache: getFromCacheACES,
} = createQuery<ProductResponse, ProductDetailsOptionsACES>(
  'productDetailsAces',
  getProductDetailsACES,
);

const getProductDetailsFromCacheACES = (
  queryClient: QueryClient,
  options: ProductDetailsOptionsACES,
): ProductData | undefined => {
  return getFromCacheACES(queryClient, options);
};

/**
 * Maps ACES properties to match ProductModel
 */
const acesSelector = (data: ProductResponse, options?: ProductDetailsOptionsACES): ProductData => {
  const {
    partGroupName,
    taxonomyPath,
    productSeoUrl,
    vehicleFit,
    vehicleFitment,
    breadCrumbs,
    categoryIdPath,
    partTerminologyId,
    partGroupId,
    displayNotesTypes,
    recordType,
    productCompareDescriptions,
  } = (data.pdpResults?.products?.[0] ?? {}) as ProductModelACES;

  const mappedBreadCrumbs = breadCrumbs?.map((breadCrumb) => {
    return {
      ...breadCrumb,
      displayName: breadCrumb.categoryName,
      seoUrl: breadCrumb.canonicalPath ?? breadCrumb.canonicalUrl,
    };
  });

  return {
    product: {
      ...data.pdpResults?.products?.[0],
      // Rename/reassign the following ACES properties that map 1:1 to match ProductModel
      partTypeName: partGroupName ?? '',
      taxonomyUrl: taxonomyPath ?? '',
      seoUrl: productSeoUrl ?? '',
      categoryId: categoryIdPath ?? '',
      vehicleFitFlag: vehicleFit === 'FITS',
      vehicleFit: vehicleFit,
      ecommProductId: partTerminologyId ?? '',
      originalPartTypeId: partTerminologyId ?? '',
      vehicleSpecificFlag: vehicleFitment === 'VEHICLE_SPECIFIC',
      breadCrumbs: mappedBreadCrumbs,
      recordType,
      partGroupId: partGroupId ?? '',
      displayNotesTypes,
      productCompareDescriptions,
      positionId: options?.positionId ?? '',
    } as ProductModel,
    redirectPageType: data.redirectPageType,
    redirectURL: data.redirectURL,
    xFusionQueryId: data.xFusionQueryId,
  };
};

// TODO: Refactor after 100% ACES/PIES transition
export const useProductDetails = <SelectData = ProductData>({
  enabled = true,
  placeholderData,
  pdpUrl,
  select,
}: {
  enabled?: boolean;
  pdpUrl?: string;
} & CustomUseQueryOptions<ProductData, SelectData> = {}) => {
  const { acesEnabled } = useAcesFlags();
  const isV2ProductsEnabled = useFeatureFlag('PDP_ELASTIC_ENABLED') === 'true';
  const locale = useLocale();
  const country = getCountryFromLocale(locale);
  const headerResult = useHeaderData();
  const storeNumber = headerResult.data?.storeNumber;
  const {
    catalogVehicleId: vehicleId, // There is no concept of vehicleId in ACES
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestionsIds,
  } = getPreferredVehicle(headerResult.data) || {};

  const router = useLegacyRouter();
  const { slug: slugFromQuery } = router.query;
  // TODO: Should be simplified to router.query.slug once https://github.com/vercel/next.js/issues/45340 is fixed

  const slug =
    slugFromQuery ??
    getPathnameFromAsPath(router.asPath)
      .split('/')
      .map((pathItem) => {
        if (pathItem !== '') {
          return pathItem.split('#')[0];
        }
      });

  const slugString = Array.isArray(slug) ? `${slug.join('/')}` : '';
  const seoUrl = getProductDetailsURL(slugString.startsWith('/') ? slugString : `/${slugString}`);

  const vehicleQuestions = extractVehicleQuestions(vehicleQuestionsIds, true) as string;

  const duplicateSkuParams = getDuplicateSkuQueryParams(router.query);

  const data = useData({
    placeholderData,
    locale,
    vehicleId: vehicleId ? Number(vehicleId) : 0,
    storeId: storeNumber ? Number(storeNumber) : undefined,
    lineCode: undefined,
    productDetailsPageUrl: pdpUrl || seoUrl,
    userSegment: undefined,
    partNumber: undefined,
    itemIds: undefined,
    preview: showXMPreviewDate(),
    enabled: !acesEnabled && enabled,
    select,
  });

  const dataACES = useDataACES({
    country,
    productDetailsPageUrl: pdpUrl || seoUrl,
    partGroupId: duplicateSkuParams.partGroupId,
    brandId: duplicateSkuParams.brandId,
    positionId: duplicateSkuParams.positionId,
    uniqueId: duplicateSkuParams.uniqueId,
    year: Number(year) || undefined,
    makeId: Number(makeId) || undefined,
    modelId: Number(modelId) || undefined,
    vehicleTypeId: Number(vehicleTypeId) || undefined,
    ...(vehicleQuestions.length > 0 && { vehicleQuestions }),
    ...(storeNumber && { storeId: Number(storeNumber) }),
    placeholderData,
    enabled: acesEnabled && enabled,
    preview: showXMPreviewDate(),
    select,
    isV2ProductsEnabled,
  });

  return acesEnabled ? dataACES : data;
};

export { getProductDetailsFromCache, prefetchProductDetails };

export { getProductDetailsFromCacheACES, prefetchProductDetailsACES };
